import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { notamsAlongRoute, notamStatus, type Notam, type NotamBulletin, type RouteNotam } from '@jupi/shared'
import { formatTime, RemoteContent, Title, useServerClock } from '@jupi/ui'
import { useAirspaceRings, useEfinNotams } from '../../api/notams'
import { useCurrentPlan, useWaypoints } from '../../api/plan'
import { NotamCard } from '../../components/notam/NotamCard'
import { utcDayAndTime } from '../../components/weather/weatherFormat'

const TABS = ['route', 'warnings', 'enroute', 'aerodromes'] as const
type NotamTab = (typeof TABS)[number]

const CORRIDORS = [5, 10, 20] as const

const byStart = (a: Notam, b: Notam) => Date.parse(a.from) - Date.parse(b.from)

// Active first, then by when they start.
function current(notams: Notam[], now: number): Notam[] {
  const live = notams.filter((n) => notamStatus(n, now) !== 'expired')
  const active = live.filter((n) => notamStatus(n, now) === 'active')
  const upcoming = live.filter((n) => notamStatus(n, now) === 'upcoming')
  // eslint-disable-next-line unicorn/no-array-sort -- sorts the fresh array filter() just built
  return [...active, ...upcoming.sort(byStart)]
}

function NotamList({ notams, now, empty }: { notams: (Notam | RouteNotam)[]; now: number; empty: string }) {
  if (notams.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ my: 2 }}>
        {empty}
      </Typography>
    )
  }
  return (
    <Stack spacing={1.5}>
      {notams.map((item) =>
        'notam' in item ? (
          <NotamCard key={item.notam.id} notam={item.notam} now={now} reason={item.reason} distanceNm={item.distanceNm} />
        ) : (
          <NotamCard key={item.id} notam={item} now={now} />
        ),
      )}
    </Stack>
  )
}

function RouteNotams({ notams, now }: { notams: Notam[]; now: number }) {
  const { t } = useTranslation()
  const [corridorNm, setCorridorNm] = useState<number>(10)
  const { data: plan, isLoading } = useCurrentPlan()
  const { data: waypoints } = useWaypoints()
  // eslint-disable-next-line unicorn/no-array-sort -- sorts the fresh array the spread just built
  const airspaceIds = useMemo(() => [...new Set(notams.flatMap((n) => n.airspaces))].sort(), [notams])
  const airspaces = useAirspaceRings(airspaceIds)

  const route = useMemo(
    () =>
      (plan?.plan.legs ?? []).flatMap((leg) =>
        leg.lat !== null && leg.lon !== null ? [{ code: leg.code, lat: leg.lat, lon: leg.lon }] : [],
      ),
    [plan],
  )
  const aerodromes = useMemo(
    () => new Map((waypoints ?? []).filter((w) => /^[A-Z]{4}$/.test(w.code)).map((w) => [w.code, w])),
    [waypoints],
  )
  const along = useMemo(
    () => notamsAlongRoute(notams, route, { corridorNm, aerodromes, airspaces }),
    [notams, route, corridorNm, aerodromes, airspaces],
  )

  if (isLoading) return null
  if (route.length === 0) {
    return (
      <Alert severity="info">
        {t('notam.noRoute')}{' '}
        <Link component={RouterLink} to="/plan">
          {t('notam.openPlan')}
        </Link>
      </Alert>
    )
  }
  const local = along.filter((r) => r.reason !== 'firWide')
  const firWide = along.filter((r) => r.reason === 'firWide')
  return (
    <Box>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {route.map((p) => p.code).join(' – ')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('notam.corridor')}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={corridorNm}
          onChange={(_, v: number | null) => v !== null && setCorridorNm(v)}
        >
          {CORRIDORS.map((nm) => (
            <ToggleButton key={nm} value={nm}>
              {nm} NM
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {airspaceIds.length > 0 && !airspaces && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('notam.loadingAreas')}
        </Typography>
      )}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('notam.alongRoute', { count: local.length })}
      </Typography>
      <NotamList notams={local} now={now} empty={t('notam.noneAlongRoute')} />
      <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
        {t('notam.firWide', { count: firWide.length })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('notam.firWideHelp')}
      </Typography>
      <NotamList notams={firWide} now={now} empty={t('notam.none')} />
    </Box>
  )
}

function AerodromeNotams({ notams, now }: { notams: Notam[]; now: number }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const groups = useMemo(() => {
    const needle = q.trim().toUpperCase()
    const byLocation = new Map<string, Notam[]>()
    for (const n of notams) {
      if (!n.location) continue
      if (needle && !n.location.includes(needle) && !(n.locationName ?? '').includes(needle)) continue
      byLocation.set(n.location, [...(byLocation.get(n.location) ?? []), n])
    }
    // eslint-disable-next-line unicorn/no-array-sort -- sorts the fresh array the spread just built
    return [...byLocation.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [notams, q])
  return (
    <Box>
      <TextField
        size="small"
        label={t('notam.searchAerodrome')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
      />
      {groups.length === 0 && <Typography color="text.secondary">{t('notam.none')}</Typography>}
      {groups.map(([code, list]) => (
        <Box key={code} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {code} {list[0]?.locationName}
          </Typography>
          <NotamList notams={list} now={now} empty="" />
        </Box>
      ))}
    </Box>
  )
}

function BulletinInfo({ bulletin }: { bulletin: NotamBulletin }) {
  const { t } = useTranslation()
  const stale = bulletin.sources.filter((s) => s.stale)
  const issued = bulletin.sources.map((s) => s.issuedAt).find(Boolean)
  const nextUpdate = bulletin.sources.map((s) => s.nextUpdate).find(Boolean)
  return (
    <>
      {stale.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('notam.stale', { time: formatTime(stale[0]?.staleSince ?? stale[0]?.retrievedAt ?? '') })}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('notam.intro')}{' '}
        {issued && t('notam.issued', { time: `${utcDayAndTime(issued)} UTC` })}{' '}
        {nextUpdate && t('notam.nextUpdate', { time: nextUpdate })}{' '}
        <Link href="https://www.ais.fi/bulletins/" target="_blank" rel="noreferrer">
          Fintraffic AIS ↗
        </Link>
      </Alert>
    </>
  )
}

// EFIN NOTAMs from Fintraffic's pre-flight information bulletins: those
// along the member's current plan, and the FIR's warnings, en-route
// information and aerodromes in full.
export default function NotamsPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const tab: NotamTab = TABS.find((x) => x === params.get('tab')) ?? 'route'
  const { data, isLoading, error } = useEfinNotams()
  const { utcMs } = useServerClock()
  // Per minute, so the route geometry is not recomputed on every clock tick.
  const now = Math.floor(utcMs / 60_000) * 60_000
  const live = useMemo(() => current(data?.notams ?? [], now), [data, now])
  const warnings = useMemo(() => live.filter((n) => n.sections.includes('warning')), [live])
  const enroute = useMemo(() => live.filter((n) => n.sections.includes('enroute')), [live])
  const aerodromeCount = useMemo(() => live.filter((n) => n.location).length, [live])

  const counts: Record<NotamTab, number | null> = {
    route: null,
    warnings: warnings.length,
    enroute: enroute.length,
    aerodromes: aerodromeCount,
  }

  return (
    <Box>
      <Title label={t('nav.planNotams')} />
      <RemoteContent isLoading={isLoading} error={error}>
        {/* Nothing until the bulletin is here: an empty list must mean no NOTAMs. */}
        {data && (
          <>
            <BulletinInfo bulletin={data} />
            <Tabs
              value={tab}
              onChange={(_, v: NotamTab) => setParams(v === 'route' ? {} : { tab: v }, { replace: true })}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {TABS.map((x) => (
                <Tab
                  key={x}
                  value={x}
                  label={counts[x] === null ? t(`notam.tab.${x}`) : `${t(`notam.tab.${x}`)} (${counts[x]})`}
                />
              ))}
            </Tabs>
            {tab === 'route' && <RouteNotams notams={live} now={now} />}
            {tab === 'warnings' && <NotamList notams={warnings} now={now} empty={t('notam.none')} />}
            {tab === 'enroute' && <NotamList notams={enroute} now={now} empty={t('notam.none')} />}
            {tab === 'aerodromes' && <AerodromeNotams notams={live} now={now} />}
          </>
        )}
      </RemoteContent>
    </Box>
  )
}
